<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { $, defineSchema } from '@bagelink/vue'
const schema = defineSchema({
	logo: $.upload('Logo', { default: '/assets/logo-dark.svg' }),
	logoAlt: $.text('Logo description', { default: 'Shalva' }),
	logoTo: $.text('Logo destination', { default: '/' }),
	surface: $.select('Background', [
		{ label: 'Light brand', value: 'light' },
		{ label: 'White surface', value: 'surface' },
		{ label: 'Brand color', value: 'brand' },
		{ label: 'Dark', value: 'dark' },
		{ label: 'Image', value: 'image' },
	], { default: 'light' }),
	backgroundImage: $.upload('Background image').if('surface eq "image"'),
	backgroundFit: $.select('Background image fit', [
		{ label: 'Cover', value: 'cover' },
		{ label: 'Contain', value: 'contain' },
		{ label: 'Fill', value: 'fill' },
	], { default: 'cover' }).if('surface eq "image" and backgroundImage pr'),
	dimBackground: $.checkbox('Dim background', { default: false }).if('surface eq "image" and backgroundImage pr'),
	links: $.array('Footer links', defineSchema({
		label: $.text('Label'),
		to: $.text('Destination'),
	}), {
		allowAdd: true,
		allowDelete: true,
		allowReorder: true,
		default: [],
	}),
	socialLinks: $.array('Social links', defineSchema({
		icon: $.icon('Icon'),
		href: $.url('Link'),
	}), {
		allowAdd: true,
		allowDelete: true,
		allowReorder: true,
		default: [
			{ href: 'https://www.youtube.com/c/SHALVAChildrensCenter/videos', icon: 'youtube' },
			{ href: 'https://www.instagram.com/shalvacenter/?hl=en', icon: 'instagram' },
			{ href: 'https://www.facebook.com/ShalvaNationalCenter/', icon: 'facebook' },
		],
	}),
})
export const EventFooter = defineBlock({
	name: 'EventFooter',
	label: 'Footer',
	icon: 'share',
	thumbnail: '/block-thumbs/EventFooter.svg',
	category: 'Site layout',
	order: 110,
	schema,
	global: true,
})
</script>
<script setup lang="ts">
import { computed } from 'vue'
import { useGlobalBlock } from '@bagelink/blox'
import { Btn, Icon, pathKeyToURL } from '@bagelink/vue'
type SocialLink = { href: string; icon?: string; label?: string }
type FooterLink = { label: string; to: string }
interface FooterContent {
	logo?: string
	logoAlt?: string
	logoTo?: string
	surface?: 'light' | 'surface' | 'brand' | 'dark' | 'image'
	backgroundImage?: string
	backgroundFit?: 'cover' | 'contain' | 'fill'
	dimBackground?: boolean
	links?: FooterLink[]
	socialLinks?: SocialLink[]
}


const footerProps = defineProps<FooterContent>()
const { data: remoteFooter } = useGlobalBlock<FooterContent>('EventFooter')

function footerValue<K extends keyof FooterContent>(
	key: K,
	fallback: NonNullable<FooterContent[K]>,
): NonNullable<FooterContent[K]> {
	const remoteValue = remoteFooter.value?.[key]
	if (remoteValue !== undefined && remoteValue !== null) {
		return remoteValue as NonNullable<FooterContent[K]>
	}
	const propValue = footerProps[key]
	if (propValue !== undefined && propValue !== null) {
		return propValue as NonNullable<FooterContent[K]>
	}
	return fallback
}

const footerLogo = computed(() => footerValue('logo', ''))
const footerLogoUrl = computed(() => pathKeyToURL(footerLogo.value))
const footerLogoAlt = computed(() => footerValue('logoAlt', ''))
const footerLogoTo = computed(() => footerValue('logoTo', ''))
const footerLinks = computed(() => footerValue('links', [])
	.filter(link => link.label?.trim() && link.to?.trim()))
const footerSocialLinks = computed(() => footerValue('socialLinks', [])
	.filter(link => link.href?.trim() && (link.icon?.trim() || link.label?.trim())))
const footerSurface = computed(() => footerValue('surface', 'light'))
const footerBackgroundImage = computed(() => footerValue('backgroundImage', ''))
const footerBackgroundFit = computed(() => footerValue('backgroundFit', 'cover'))
const footerDimBackground = computed(() => footerValue('dimBackground', false))
const autoLightLogo = computed(() => (
	footerLogo.value === '/assets/logo-dark.svg'
	&& ['brand', 'dark'].includes(footerSurface.value)
))
const footerIconColor = computed(() => ['brand', 'dark'].includes(footerSurface.value) ? 'white' : 'primary')
const footerSurfaceClass = computed(() => ({
	light: 'bg-primary-light',
	surface: 'bg-box',
	brand: 'bg-primary color-white keep-white',
	dark: 'bg-black color-white keep-white',
	image: 'color-white keep-white',
}[footerSurface.value]))
const footerStyle = computed(() => footerSurface.value === 'image' && footerBackgroundImage.value
	? {
			backgroundImage: `${footerDimBackground.value ? 'linear-gradient(#0006, #0006), ' : ''}url("${pathKeyToURL(footerBackgroundImage.value) || footerBackgroundImage.value}")`,
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			backgroundSize: footerBackgroundFit.value === 'fill' ? '100% 100%' : footerBackgroundFit.value,
		}
	: undefined)
</script>

<template>
	<footer
		class="event-footer txt-20 line-height-16 txt-center px-1 py-2 m_py-1"
		:class="footerSurfaceClass"
		:style="footerStyle"
	>
		<div class="w-1000px grid gap-1">
			<RouterLink v-if="footerLogoUrl && footerLogoTo" :to="footerLogoTo" :aria-label="footerLogoAlt">
				<img
					:src="footerLogoUrl"
					:alt="footerLogoAlt"
					class="w-100p w-240px"
					:class="{ 'event-footer-logo-auto-light': autoLightLogo }"
					loading="lazy"
				>
			</RouterLink>
			<img
				v-else-if="footerLogoUrl"
				:src="footerLogoUrl"
				:alt="footerLogoAlt"
				class="w-100p w-240px"
				:class="{ 'event-footer-logo-auto-light': autoLightLogo }"
				loading="lazy"
			>
			<nav v-if="footerLinks.length" aria-label="Footer" class="flex flex-wrap gap-1 justify-content-center">
				<RouterLink v-for="link in footerLinks" :key="link.to" :to="link.to">{{ link.label }}</RouterLink>
			</nav>
			<div v-if="footerSocialLinks.length" class="flex gap-05 justify-content-center">
				<Btn
					v-for="(link, index) in footerSocialLinks"
					:key="`${link.href}-${index}`"
					flat
					round
					:color="footerIconColor"
					:href="link.href"
					target="_blank"
					rel="noopener noreferrer"
					:aria-label="link.label || link.icon || 'Social link'"
				>
					<Icon :name="link.icon || link.label?.toLowerCase()" :size="1.4" />
				</Btn>
			</div>
		</div>
	</footer>
</template>
